/**
 * Domain enums and defaults shared by API and frontends.
 * These mirror the Prisma enums in packages/db/prisma/schema.prisma —
 * keep both in sync (there is a test asserting they match).
 */
export const USER_SCOPES = ['PLATFORM', 'TENANT_STAFF', 'CUSTOMER'];
export const PLATFORM_ROLES = ['PLATFORM_ADMIN'];
export const STAFF_ROLES = ['OWNER', 'MANAGER', 'OPERATOR', 'SUPPORT'];
export const USER_ROLES = [...PLATFORM_ROLES, ...STAFF_ROLES];
export const TENANT_STATUSES = ['ONBOARDING', 'ACTIVE', 'SUSPENDED'];
export const DOMAIN_TYPES = ['SUBDOMAIN', 'CUSTOM'];
/**
 * Navigation shapes a tenant's passenger app can take (DECISIONS D69 — the platform owns
 * the shape of the app). Each is a different structural answer, not a restyling of the
 * same bar: where navigation lives decides what the app feels like on a phone.
 *
 * - BOTTOM_TABS   grounded, edge to edge, under the thumb. The safe default.
 * - FLOATING_DOCK a detached island the page scrolls beneath. Fewer destinations, more air.
 * - SIDE_DRAWER   behind a header button. The one that scales past five pages.
 * - TOP_BAR       always visible above the content; the chips scroll on a narrow screen.
 * - RAIL          a persistent icon rail on a wide screen, a dock on a narrow one.
 */
export const MENU_STYLES = [
    'BOTTOM_TABS',
    'FLOATING_DOCK',
    'SIDE_DRAWER',
    'TOP_BAR',
    'RAIL',
];
export const THEME_MODES = ['LIGHT', 'DARK', 'AUTO'];
/** Fonts a tenant may choose (doc 03). Anything else is rejected at validation. */
export const ALLOWED_FONTS = ['system', 'Inter', 'Roboto', 'Poppins', 'Lora'];
/** Menu item keys map to fixed customer-app routes (doc 04 §4). */
export const MENU_ITEM_KEYS = [
    'search',
    'bookings',
    'travellers',
    'profile',
    'offers',
    'help',
    'about',
];
/**
 * Customer-app features an operator can show or hide (doc 04 — tenant theming).
 *
 * The same idea as MENU_ITEM_KEYS, for capability rather than navigation: an operator
 * decides which of these their passengers get, and in what order the ones that render as
 * actions appear on a trip card.
 */
export const CUSTOMER_FEATURE_KEYS = [
    'emailTicket',
    'travellers',
    'rebook',
    'calendar',
    'offlineTicket',
    'liveStatus',
];
/**
 * What a tenant gets before anyone opens the screen.
 *
 * Everything on, deliberately. A tenant that has never configured this must not be
 * silently missing functionality, and it means switching a feature off is always a
 * decision somebody made rather than a row nobody wrote. It also removes the need to
 * backfill every existing tenant when this ships.
 */
export const DEFAULT_CUSTOMER_FEATURES = [
    { key: 'emailTicket', label: 'Email ticket', visible: true },
    { key: 'travellers', label: 'Travellers', visible: true },
    { key: 'rebook', label: 'Book again', visible: true },
    { key: 'calendar', label: 'Add to calendar', visible: true },
    { key: 'offlineTicket', label: 'Offline ticket', visible: true },
    { key: 'liveStatus', label: 'Live status', visible: true },
];
export const SEAT_CELL_TYPES = ['SEAT', 'AISLE', 'DRIVER', 'DOOR'];
export const SEAT_STATES = ['FREE', 'HELD', 'BOOKED'];
export const TRIP_STATUSES = ['SCHEDULED', 'DELAYED', 'DEPARTED', 'CANCELLED'];
export const BOOKING_STATUSES = [
    'PENDING_PAYMENT',
    'CONFIRMED',
    'CANCELLED',
    'REFUNDED',
    'EXPIRED',
];
export const PAYMENT_PROVIDERS = ['STRIPE', 'MOCK', 'BARAKAPAY'];
export const PAYMENT_STATUSES = ['PENDING', 'SUCCEEDED', 'FAILED', 'REFUNDED'];
export const DISPUTE_TYPES = ['REFUND_REQUEST', 'COMPLAINT', 'OTHER'];
export const DISPUTE_STATUSES = [
    'OPEN',
    'IN_REVIEW',
    'RESOLVED_REFUNDED',
    'RESOLVED_REJECTED',
    'CLOSED',
];
export const DISPUTE_AUTHORS = ['CUSTOMER', 'STAFF'];
export const NOTIFICATION_TYPES = [
    'BOOKING_CONFIRMED',
    'PASSWORD_RESET',
    'STAFF_INVITE',
    'TRIP_DELAYED',
    'TRIP_CANCELLED',
    'TRIP_CHANGED',
    'DEPARTURE_REMINDER',
    'DISPUTE_UPDATED',
];
/** Whether the outbox knows how to render and send this type. */
export function isDispatchableNotification(type) {
    return NOTIFICATION_TYPES.includes(type);
}
export const NOTIFICATION_CHANNELS = ['PUSH', 'EMAIL'];
export const NOTIFICATION_STATUSES = ['PENDING', 'SENT', 'FAILED'];
export const CONTRACT_TYPES = ['COMMISSION', 'MONTHLY', 'HYBRID'];
export const INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'VOID'];
/** Onboarding checklist step keys (doc 07). */
export const ONBOARDING_STEPS = [
    'company_info',
    'branding_uploaded',
    'first_route',
    'first_timetable',
    'payment_settings',
    // Certificates are issued per host over HTTP-01 by a script run on the proxy box, so this is
    // manual and stays manual until there is a wildcard. Tracked but not required for go-live —
    // see the note in the API's onboarding module for why an unverifiable tick must not gate it.
    'tls_certificates',
    'contract_signed',
    'go_live',
];
/** Steps the system marks done automatically by observing tenant data. */
export const AUTO_ONBOARDING_STEPS = [
    'branding_uploaded',
    'first_route',
    'first_timetable',
];
// --- Defaults (doc 03 tenants.settings) ------------------------------------
/** Languages the passenger app ships. Empty tenant locale = follow the browser. */
export const PASSENGER_LOCALES = ['en', 'fr', 'sw', 'ln'];
export const DEFAULT_TENANT_SETTINGS = {
    /**
     * The language this operator's passengers see first. Empty means follow the
     * browser — which is the wrong default for an operator whose passengers carry
     * phones set to English because that is what shipped on them.
     */
    defaultLocale: '',
    reminderLeadMinutes: 60,
    paymentWindowMinutes: 15,
    cancellationCutoffHours: 24,
    maxSeatsPerBooking: 6,
    supportEmail: '',
    supportPhone: '',
    aboutHtml: '',
    faqHtml: '',
    /**
     * Who the operator is, legally. Printed on the ticket, where a passenger claiming a
     * refund or an employer filing a receipt needs to know who sold the journey. Blank
     * renders nothing rather than a labelled empty line.
     */
    companyLegalName: '',
    registrationNumber: '',
    taxId: '',
    companyAddress: '',
};
export const SEAT_HOLD_TTL_MINUTES = 10;
export const REMINDER_LEAD_MIN_MINUTES = 15;
export const REMINDER_LEAD_MAX_MINUTES = 1440;
export const DEFAULT_TRIP_HORIZON_DAYS = 60;
export const PAGE_SIZE_DEFAULT = 20;
export const PAGE_SIZE_MAX = 100;
/** Minimum WCAG AA contrast ratio enforced when saving branding colours. */
export const MIN_CONTRAST_RATIO = 4.5;
//# sourceMappingURL=constants.js.map